import * as Api from '@ViewModels';
import { Badge } from '@WebComponents/dataBoards/Badge';
import { DataGrid, DataGridRow, DataGridRowItem, DataGridRows } from '@WebComponents/DataGrid';
import { styleSheet as flyoutStyleSheet } from '@WebComponents/dataBoards/styles';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { DonationIcon } from '@WebComponents/svgs/icons/DonationIcon';
import { SimpleTooltip } from '@WebComponents/Tooltip';
import { s as tooltipStyles } from '@WebComponents/Tooltip/styles';
import { Button } from '../../../../components-v2/Button';
import { DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '../../../../components-v2/Drawer';
import { ExpandableText } from '../../../../components-v2/ExpandableText';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { numberToCurrencyStringValue } from '../../../../../models/UiUtils';
import { useDonationFilterQuery, useDonorFilterQuery } from '../../../../../queries';
import { styleSheet as dbStyleSheet } from '../../../../containers/dataBoards/styles';
import { styleSheet } from './styles';

export const DonorDetailsFlyout = ({ contactId }: { contactId: string }) => {
	const donorFilterQuery = useDonorFilterQuery({
		enabled: !!contactId,
		filterRequest: {
			criteria: [
				{
					property: Api.DonorFilterProperty.ContactId,
					value: contactId,
				},
			],
		},
	});

	const donationFilterQuery = useDonationFilterQuery({
		enabled: !!contactId,
		filterRequest: {
			criteria: [
				{
					property: Api.DonationFilterProperty.ContactId,
					value: contactId,
				},
			],
		},
		expand: 'contact,TagsCollection',
	});

	const historyData = React.useMemo(
		() => donationFilterQuery.data?.pages.map(page => page.values).flat() ?? [],
		[donationFilterQuery.data?.pages]
	);
	const totalCount = React.useMemo(
		() => donationFilterQuery.data?.pages?.[0]?.totalCount ?? 0,
		[donationFilterQuery.data?.pages]
	);

	const donor = React.useMemo(() => {
		return donorFilterQuery.data?.pages.map(page => page.values).flat()[0] ?? null;
	}, [donorFilterQuery.data?.pages]);

	return (
		<DrawerContent>
			<DrawerHeader>
				<DrawerTitle>Donor Details</DrawerTitle>
			</DrawerHeader>
			<div className={css(flyoutStyleSheet.flyoutBody, styleSheet.body)}>
				{donorFilterQuery.isPending ? <LoadingSpinner type='large' /> : null}
				{!donorFilterQuery.isPending ? (
					<div className={css(styleSheet.container)}>
						<section className={css(styleSheet.section)}>
							<h4 className={css(flyoutStyleSheet.subTitle)}>{donor.donorName}</h4>
							<small>{donor.contact?.primaryEmail?.value}</small>
						</section>

						<div className={css(styleSheet.section)}>
							<label className={css(styleSheet.label)}>Most Recent Donation</label>
							<DataGrid styles={[styleSheet.tableOverrides, styleSheet.tableFixed]}>
								<DataGridRows>
									<DataGridRow>
										<DataGridRowItem noTopBorder>
											<span className={css(dbStyleSheet.amountText)}>
												${numberToCurrencyStringValue(donor.mostRecentDonation.amount)}
											</span>
										</DataGridRowItem>
										<DataGridRowItem noTopBorder>
											{donor.mostRecentDonation.campaign ? (
												<SimpleTooltip
													title={donor.mostRecentDonation.campaign.name}
													delayDuration={0}
													styles={[tooltipStyles.contentWithMaxWidth]}
												>
													<div>
														<Badge
															text={donor.mostRecentDonation.campaign.name}
															foregroundColor={donor.mostRecentDonation.campaign.foregroundColor}
															truncate
														/>
													</div>
												</SimpleTooltip>
											) : null}
										</DataGridRowItem>
										<DataGridRowItem noTopBorder>
											<span>{moment(donor.mostRecentDonation.date).format('MM/DD/YYYY')}</span>
										</DataGridRowItem>
									</DataGridRow>
								</DataGridRows>
							</DataGrid>
						</div>

						<section className={css(styleSheet.lifetimeSection)}>
							<DonationIcon />
							<div className={css(styleSheet.stackedText)}>
								<h6 className={css(styleSheet.sectionHeader)}>Life Time Donations</h6>
								<span className={css(dbStyleSheet.amountText)}>
									${numberToCurrencyStringValue(donor.lifetimeDonations)}
								</span>
							</div>
						</section>

						{donor.contact ? (
							<section className={css(styleSheet.section)}>
								<h6 className={css(styleSheet.sectionHeader)}>Donation History ({totalCount})</h6>

								{donationFilterQuery.isPending ? <LoadingSpinner type='large' /> : null}

								{!donationFilterQuery.isPending ? (
									<DataGrid
										data-testid='donation-history-grid'
										styles={[styleSheet.tableOverrides, styleSheet.tableFixed]}
									>
										<DataGridRows>
											{historyData.map((datum, index) => (
												<React.Fragment key={`donation-${datum.id}`}>
													<DataGridRow>
														<DataGridRowItem noTopBorder={index === 0}>
															<span className={css(dbStyleSheet.amountText)}>
																${numberToCurrencyStringValue(datum.amount)}
															</span>
														</DataGridRowItem>
														<DataGridRowItem noTopBorder={index === 0}>
															{datum.campaign ? (
																<SimpleTooltip
																	title={datum.campaign.name}
																	delayDuration={0}
																	styles={[tooltipStyles.contentWithMaxWidth]}
																>
																	<div>
																		<Badge
																			text={datum.campaign.name}
																			foregroundColor={datum.campaign.foregroundColor}
																			truncate
																		/>
																	</div>
																</SimpleTooltip>
															) : null}
														</DataGridRowItem>
														<DataGridRowItem noTopBorder={index === 0}>
															<span>{moment(datum.date).format('MM/DD/YYYY')}</span>
														</DataGridRowItem>
													</DataGridRow>
													{datum.details && (
														<DataGridRow>
															<DataGridRowItem colSpan={3} noTopBorder>
																<ExpandableText text={datum.details} prefix='Details: ' />
															</DataGridRowItem>
														</DataGridRow>
													)}
												</React.Fragment>
											))}
											{donationFilterQuery.hasNextPage ? (
												<DataGridRow>
													<DataGridRowItem colSpan={3}>
														{donationFilterQuery.isPending || donationFilterQuery.isFetchingNextPage ? (
															<LoadingSpinner type='large' />
														) : (
															<Waypoint bottomOffset='-100px' onEnter={() => donationFilterQuery.fetchNextPage()} />
														)}
													</DataGridRowItem>
												</DataGridRow>
											) : null}
										</DataGridRows>
									</DataGrid>
								) : null}
							</section>
						) : null}
					</div>
				) : null}
			</div>
			<DrawerFooter>
				<DrawerClose
					render={props => (
						<Button variant='secondary' {...props}>
							Close
						</Button>
					)}
				/>
			</DrawerFooter>
		</DrawerContent>
	);
};
